// Section12.jsx — Cross-Platform Mobile App Development
import React from 'react';
import { useReveal } from '../../componets/useScrollFX';
const crossBanner = '../assets/Cross-Platform Mobile App Development.webp';
const flutter_icon = '../assets/icons8-flutter.svg';
const react_icon = '../assets/icons8-react.svg';
const ionic_icon = '../assets/icons8-ionic.svg';

const Section12 = () => {
  const [headRef, headVisible] = useReveal();
  const [bannerRef, bannerVisible] = useReveal();
  const [cardsRef, cardsVisible] = useReveal();
  const [advRef, advVisible] = useReveal();

  const advantages = [
    "Lower development costs",
    "Faster time-to-market",
    "Simplified maintenance",
    "Unified user experience",
    "Easier feature updates",
  ];

  return (
    <section id="cross-platform" className="bg-white py-10 md:py-10 border-t border-gray-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div
          ref={headRef}
          className={`text-center mb-12 transition-all duration-700 ${
            headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="text-[#cd2122] text-xs font-semibold uppercase tracking-widest">Cross-Platform</span>
          <h2 className="text-[32px] md:text-[38px] font-extrabold text-[#1a1a1a] mt-3 mb-4 leading-tight">
            Cross-Platform Mobile App Development
          </h2>
          <p className="text-[#535353] text-[16px] max-w-2xl mx-auto">
            Many businesses need efficient ways to reach users across multiple platforms. Cross-platform mobile app development enables organizations to launch applications for both Android and iOS while reducing development time and costs.
          </p>
        </div>

        {/* Banner strip with center VS badge */}
        <div
          ref={bannerRef}
          className={`relative rounded-3xl overflow-hidden shadow-xl mb-5 max-w-5xl mx-auto transition-all duration-700 delay-100 ${
            bannerVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <img src={crossBanner} alt="Cross-platform mobile app development" className="w-full h-[260px] object-cover" />
        </div>
        <p className="text-[#000] text-[16px] max-w-2xl mx-auto text-center my-12 font-bold">
          Mean3 specializes in modern cross-platform frameworks that deliver native-like experiences.
        </p>

        <div ref={cardsRef} className="grid md:grid-cols-2 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {/* Flutter Card */}
          <div
            className={`relative bg-gradient-to-br from-[#fafafa] to-white border border-gray-100 rounded-3xl p-9 hover:border-[#cd2122] hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ${
              cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="absolute top-6 right-6 text-[11px] font-semibold text-[#cd2122] bg-[#cd2122]/10 px-2.5 py-1 rounded-full">01</span>
            <div className="text-[#cd2122] text-4xl mb-6">
              <img src={flutter_icon} alt="" width={70} />
            </div>
            <h3 className="text-2xl font-bold text-[#1a1a1a] mb-4">Flutter Development</h3>
            <p className="text-[#535353] text-[16px] max-w-2xl mb-3">
              Flutter allows us to build visually rich applications with:
            </p>
            <ul className="text-[16px] text-[#535353] space-y-3">
              <li>• Fast development cycles</li>
              <li>• Consistent user interfaces</li>
              <li>• Excellent performance</li>
              <li>• Reduced maintenance</li>
            </ul>
          </div>

          {/* React Native Card */}
          <div
            style={{ transitionDelay: '150ms' }}
            className={`relative bg-gradient-to-br from-[#fafafa] to-white border border-gray-100 rounded-3xl p-9 hover:border-[#cd2122] hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ${
              cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="absolute top-6 right-6 text-[11px] font-semibold text-[#cd2122] bg-[#cd2122]/10 px-2.5 py-1 rounded-full">02</span>
            <div className="text-[#cd2122] text-4xl mb-6">
              <img src={react_icon} alt="" width={70} />
            </div>
            <h3 className="text-2xl font-bold text-[#1a1a1a] mb-4">React Native Development</h3>
            <p className="text-[#535353] text-[16px] max-w-2xl mb-3">
              React Native offers:
            </p>
            <ul className="text-[16px] text-[#535353] space-y-3">
              <li>• Code reusability</li>
              <li>• Faster deployment</li>
              <li>• Scalable architecture</li>
              <li>• Strong ecosystem</li>
            </ul>
          </div>

          {/* Ionic Card */}
          <div
            style={{ transitionDelay: '300ms' }}
            className={`relative bg-gradient-to-br from-[#fafafa] to-white border border-gray-100 rounded-3xl p-9 hover:border-[#cd2122] hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ${
              cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="absolute top-6 right-6 text-[11px] font-semibold text-[#cd2122] bg-[#cd2122]/10 px-2.5 py-1 rounded-full">03</span>
            <div className="text-[#cd2122] text-4xl mb-6">
              <img src={ionic_icon} alt="" width={70} />
            </div>
            <h3 className="text-2xl font-bold text-[#1a1a1a] mb-4">Ionic Development</h3>
            <p className="text-[#535353] text-[16px] max-w-2xl mb-3">
              Ionic offers:
            </p>
            <ul className="text-[16px] text-[#535353] space-y-3">
              <li>• Cross-platform development with a single codebase</li>
              <li>• Fast and cost-effective app development</li>
              <li>• Native device access through Capacitor plugins</li>
              <li>• Seamless deployment for Android, iOS, and web</li>
              <li>• Easy maintenance and scalability</li>
            </ul>
          </div>
        </div>

        {/* Advantages of Cross-Platform Development */}
        <div
          ref={advRef}
          className={`relative max-w-6xl mx-auto mt-16 rounded-3xl overflow-hidden bg-gradient-to-br from-[#cd2122] via-[#b91d1f] to-[#7a1314] transition-all duration-700 ${
            advVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-10"
            style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '24px 24px' }}
          />
          <div className="relative grid md:grid-cols-2 gap-10 p-9 md:p-14">
            <div>
              <span className="inline-block text-white/80 text-xs font-semibold uppercase tracking-widest mb-3">Why It Works</span>
              <h3 className="text-white text-[26px] md:text-[32px] font-extrabold leading-tight mb-5">
                Advantages of Cross-Platform Development
              </h3>
              <p className="text-white/85 text-[16px] leading-relaxed mb-6">
                Businesses benefit from:
              </p>
              <p className="text-white/90 text-[16px] leading-relaxed">
                Our team evaluates your goals and recommends the most effective development approach for your project.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {advantages.map((item, i) => (
                <div
                  key={i}
                  style={{ transitionDelay: `${i * 80}ms` }}
                  className={`flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-4 hover:bg-white/15 transition-all duration-500 ${
                    advVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                >
                  <span className="w-7 h-7 flex-shrink-0 rounded-full bg-white text-[#cd2122] flex items-center justify-center text-sm font-bold">✓</span>
                  <p className="text-white text-[15px] font-medium">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Section12;